import { Fragment, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { X } from 'lucide-react';
import type { AppOpenBubbleDTO, NudgeDTO } from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import type { Emotion } from '../emotion';
import { Markdown } from '../markdown';
import { prefersReducedMotion } from '../useTurn';

/** D73: the bubble hides itself after this long. */
export const BUBBLE_MS = 8_000;

/** A nudge's word (D73), or the briefing's opening sentence with its bold parts (L3 D123). */
export type BubblePick =
  | { kind: 'nudge'; id: Id; text: string; emotion: Emotion }
  | { kind: 'briefing'; id: Id; text: string; strong: readonly string[]; emotion: Emotion };

/**
 * What the bubble says. L3 D123: the briefing's opening sentence the server granted comes first. Then D73: the newest
 * `high` nudge (Hana worried), else the newest reminder (Hana asks); nothing for normal and low ones.
 */
export function pickBubble(items: readonly NudgeDTO[], briefing: AppOpenBubbleDTO | null = null): BubblePick | null {
  if (briefing !== null) {
    return { kind: 'briefing', id: briefing.briefingId, text: briefing.text, strong: briefing.strong, emotion: 'greet' };
  }
  const newest = (list: NudgeDTO[]) => list.reduce<NudgeDTO | null>((best, n) =>
    (best === null || Date.parse(n.shownAt) > Date.parse(best.shownAt) ? n : best), null);
  const high = newest(items.filter((n) => n.severity === 'high'));
  if (high !== null) return { kind: 'nudge', id: high.id, text: high.text, emotion: 'worried' };
  const reminder = newest(items.filter((n) => n.reminder));
  return reminder === null ? null : { kind: 'nudge', id: reminder.id, text: reminder.text, emotion: 'ask' };
}

/**
 * The sentence cut at its bold parts (D123 `strong`): each part is found in order, after the previous one, so a part
 * that also reads inside an earlier one ("3 việc" after "3 việc quá hạn") bolds its own place. A part not found is
 * left plain.
 */
export function boldParts(text: string, strong: readonly string[]): { text: string; bold: boolean }[] {
  const out: { text: string; bold: boolean }[] = [];
  let at = 0;
  for (const part of strong) {
    if (part === '') continue;
    const i = text.indexOf(part, at);
    if (i < 0) continue;
    if (i > at) out.push({ text: text.slice(at, i), bold: false });
    out.push({ text: part, bold: true });
    at = i + part.length;
  }
  if (at < text.length) out.push({ text: text.slice(at), bold: false });
  return out;
}

const SEEN_PREFIX = 'hana.bubble.';
/** A sessionStorage that throws: this page's own memory holds the one bubble instead. */
const seenHere = new Set<Id>();

/** D73: one bubble per session per person, kept in sessionStorage (read and written in try/catch). */
export function bubbleSeen(userId: Id): boolean {
  try {
    return sessionStorage.getItem(`${SEEN_PREFIX}${userId}`) !== null;
  } catch {
    return seenHere.has(userId);
  }
}

export function markBubbleSeen(userId: Id): void {
  try {
    sessionStorage.setItem(`${SEEN_PREFIX}${userId}`, '1');
  } catch {
    seenHere.add(userId);
  }
}

const SHELL = 'hana-bubble absolute right-0 top-11 z-40 flex w-[290px] items-start gap-1 rounded-xl border '
  + 'border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] py-2.5 pl-3 pr-1.5 text-[13px] leading-[1.45] '
  + 'text-[hsl(var(--h-card-foreground))] shadow-[var(--h-shadow-lg)] before:absolute before:-top-1.5 '
  + "before:right-[14px] before:size-2.5 before:rotate-45 before:border-l before:border-t "
  + "before:border-[hsl(var(--h-border))] before:bg-[hsl(var(--h-card))] before:content-['']";

/**
 * Hana's short word beside the header button (spec §7.4, D73; L3 §8.5, D123). A nudge's text is one button that opens
 * the panel, its object chips read as plain bold words. The briefing's sentence reads with its bold parts and the link
 * "Xem trên Dashboard →" (`onDashboard`), where the card is. It hides itself after `BUBBLE_MS`, and comes in with no
 * movement under reduced motion. The caller renders it from lg only.
 */
export function HanaBubble({ pick, still, onOpen, onDashboard, onClose }: {
  pick: BubblePick; still: boolean; onOpen: () => void; onDashboard: () => void; onClose: () => void;
}) {
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; });
  useEffect(() => {
    const timer = window.setTimeout(() => close.current(), BUBBLE_MS);
    return () => window.clearTimeout(timer);
  }, []);
  const motion = !still && !prefersReducedMotion();
  return (
    <div data-hana-bubble={pick.kind} data-motion={motion ? '' : undefined} role="status" className={SHELL}>
      {pick.kind === 'nudge' ? (
        <button type="button" onClick={onOpen}
          className="min-w-0 flex-1 text-left [&_p]:m-0">
          <Markdown text={pick.text} renderObject={(o) => <b className="font-bold">{o.label}</b>} />
        </button>
      ) : (
        <div className="min-w-0 flex-1 py-px">
          <p className="m-0">
            {boldParts(pick.text, pick.strong).map((part, i) => (part.bold
              ? <b key={i} className="font-bold">{part.text}</b> : <Fragment key={i}>{part.text}</Fragment>))}
          </p>
          <Link to="/" onClick={onDashboard}
            className="mt-1.5 inline-flex rounded-sm text-xs font-semibold text-[hsl(var(--h-primary))] hover:underline">
            Xem trên Dashboard →
          </Link>
        </div>
      )}
      <button type="button" aria-label="Ẩn lời nhắc" onClick={onClose}
        className="flex size-6 flex-none items-center justify-center rounded-md text-[hsl(var(--h-muted-foreground))] transition-colors hover:bg-[hsl(var(--h-muted))]">
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
